"use client";

import { create } from "zustand";

import type { ScheduleWindow } from "@/lib/api/model/scheduleWindow";
import type { SequenceRulesInterestedAction } from "@/lib/api/model/sequenceRulesInterestedAction";
import type { SequenceRulesNotInterestedAction } from "@/lib/api/model/sequenceRulesNotInterestedAction";

/**
 * Step ordinals match the wizard's left-rail labels (see Figma `218:2316`).
 * We keep the indicator 1-based so it lines up with the rendered rail
 * labels — clamps + comparisons all use these constants.
 */
export type WizardStep = 1 | 2 | 3;

export interface WizardDetailsValues {
  name: string;
  sender_user_id: string | null;
  mailbox_id: string | null;
}

/**
 * Schedule values mirror `SequenceSchedule` but keep `windows` non-nullable
 * so the form renderer can iterate without null guards. The Mon-first list
 * is reordered to Sun-first at render time — the spec is order-insensitive.
 */
export interface WizardScheduleValues {
  timezone: string;
  windows: ScheduleWindow[];
  skip_holidays: boolean;
}

export interface WizardRulesValues {
  pause_on_out_of_office: boolean;
  interested_action: SequenceRulesInterestedAction;
  interested_stage_id: string | null;
  interested_auto_draft_enabled: boolean;
  interested_auto_draft_instructions: string;
  not_interested_action: SequenceRulesNotInterestedAction;
  not_interested_stage_id: string | null;
  not_interested_auto_draft_enabled: boolean;
  not_interested_auto_draft_instructions: string;
  cc_emails: string[];
  bcc_emails: string[];
}

/** Whether the wizard is creating a fresh sequence or cloning an existing one. */
export type WizardMode = "create" | "clone";

export interface WizardClonePrefill {
  jobId: string;
  sourceId: string;
  sourceName: string;
  details: WizardDetailsValues;
  schedule: WizardScheduleValues;
  rules: WizardRulesValues;
}

interface WizardState {
  open: boolean;
  jobId: string | null;
  mode: WizardMode;
  /** Source sequence id when `mode === "clone"`; null otherwise. */
  cloneSourceId: string | null;
  /** Source sequence name for the "Cloning …" banner. */
  cloneSourceName: string | null;
  step: WizardStep;
  details: WizardDetailsValues;
  schedule: WizardScheduleValues;
  rules: WizardRulesValues;
  isDirty: boolean;
  pendingDiscard: boolean;
  /**
   * Job to open the create wizard for after navigation lands on that job's
   * sequences page. Used when starting create from the project sheet so we
   * don't open the modal on a different job's sequences route.
   */
  pendingOpenJobId: string | null;

  openWizard: (jobId: string) => void;
  /**
   * Queue a create-wizard open for `jobId` without showing the modal yet.
   * Call {@link consumePendingOpen} on the destination sequences page.
   */
  queueOpenWizard: (jobId: string) => void;
  /** If `jobId` matches the queued pending open, open the wizard and clear it. */
  consumePendingOpen: (jobId: string) => boolean;
  openClone: (prefill: WizardClonePrefill) => void;
  close: () => void;
  setStep: (step: WizardStep) => void;
  setDetails: (
    next: Partial<WizardDetailsValues>,
    options?: { dirty?: boolean },
  ) => void;
  setSchedule: (
    next: Partial<WizardScheduleValues>,
    options?: { dirty?: boolean },
  ) => void;
  setRules: (
    next: Partial<WizardRulesValues>,
    options?: { dirty?: boolean },
  ) => void;
  setPendingDiscard: (pending: boolean) => void;
  reset: () => void;
}

export const ALL_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

const DEFAULT_DETAILS: WizardDetailsValues = {
  name: "",
  sender_user_id: null,
  mailbox_id: null,
};

const DEFAULT_SCHEDULE: WizardScheduleValues = {
  timezone: "America/New_York",
  windows: ALL_DAYS.map((day) => ({
    day,
    enabled: day !== "sat" && day !== "sun",
    start: "09:00",
    end: "17:00",
  })),
  skip_holidays: true,
};

const DEFAULT_RULES: WizardRulesValues = {
  pause_on_out_of_office: true,
  interested_action: "next_stage",
  interested_stage_id: null,
  interested_auto_draft_enabled: true,
  interested_auto_draft_instructions: "",
  not_interested_action: "archive",
  not_interested_stage_id: null,
  not_interested_auto_draft_enabled: true,
  not_interested_auto_draft_instructions: "",
  cc_emails: [],
  bcc_emails: [],
};

const initialState = {
  open: false,
  jobId: null as string | null,
  mode: "create" as WizardMode,
  cloneSourceId: null as string | null,
  cloneSourceName: null as string | null,
  step: 1 as WizardStep,
  details: { ...DEFAULT_DETAILS },
  schedule: {
    ...DEFAULT_SCHEDULE,
    windows: DEFAULT_SCHEDULE.windows.map((w) => ({ ...w })),
  },
  rules: { ...DEFAULT_RULES },
  isDirty: false,
  pendingDiscard: false,
  pendingOpenJobId: null as string | null,
};

function freshSchedule() {
  return {
    ...DEFAULT_SCHEDULE,
    windows: DEFAULT_SCHEDULE.windows.map((w) => ({ ...w })),
  };
}

/**
 * Ephemeral wizard state. The wizard is intentionally local-only — no draft
 * is persisted on the server (per spec §5: "The wizard is intentionally not
 * persisted as a draft on the server"). Closing the Modal clears state.
 */
export const useSequenceWizardStore = create<WizardState>((set, get) => ({
  ...initialState,
  openWizard: (jobId) =>
    set({
      ...initialState,
      schedule: freshSchedule(),
      open: true,
      jobId,
      pendingOpenJobId: null,
    }),
  queueOpenWizard: (jobId) =>
    set({
      pendingOpenJobId: jobId,
      open: false,
    }),
  consumePendingOpen: (jobId) => {
    if (get().pendingOpenJobId !== jobId) return false;
    get().openWizard(jobId);
    return true;
  },
  openClone: (prefill) =>
    set({
      ...initialState,
      open: true,
      jobId: prefill.jobId,
      mode: "clone",
      cloneSourceId: prefill.sourceId,
      cloneSourceName: prefill.sourceName,
      details: { ...prefill.details },
      schedule: {
        ...prefill.schedule,
        windows: prefill.schedule.windows.map((w) => ({ ...w })),
      },
      rules: { ...prefill.rules },
      pendingOpenJobId: null,
    }),
  close: () =>
    set({
      ...initialState,
      schedule: freshSchedule(),
    }),
  setStep: (step) => set({ step }),
  setDetails: (next, options) =>
    set((state) => ({
      details: { ...state.details, ...next },
      isDirty: state.isDirty || (options?.dirty ?? true),
    })),
  setSchedule: (next, options) =>
    set((state) => ({
      schedule: { ...state.schedule, ...next },
      isDirty: state.isDirty || (options?.dirty ?? true),
    })),
  setRules: (next, options) =>
    set((state) => ({
      rules: { ...state.rules, ...next },
      isDirty: state.isDirty || (options?.dirty ?? true),
    })),
  setPendingDiscard: (pendingDiscard) => set({ pendingDiscard }),
  reset: () =>
    set({
      ...initialState,
      schedule: freshSchedule(),
    }),
}));
